import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import { ItemsDto } from '../../../../types/items';
import {
  ITEM_TYPES,
  ItemTypeChangePreview,
  getItemTypeLabel,
} from '../../../../types/itemTypeChange';
import { itemTypeChangeService } from '../../../../services/itemTypeChangeService';

interface Step2Props {
  item: ItemsDto;
  onPreviewReady: (newType: number, preview: ItemTypeChangePreview) => void;
  onBack: () => void;
}

export const Step2SelectNewType: React.FC<Step2Props> = ({ item, onPreviewReady, onBack }) => {
  const [selected, setSelected] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentType = item.itemType ?? 0;

  const handleNext = async () => {
    if (selected === null) return;
    try {
      setLoading(true);
      setError(null);
      const preview = await itemTypeChangeService.getPreview(item.itemNo, selected);
      onPreviewReady(selected, preview);
    } catch (e: any) {
      setError(e?.message || 'Kunne ikke hente forhåndsvisning');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Steg 2: Velg ny varetype</h2>
        <div className="bg-gray-50 p-3 rounded-md mb-4">
          <p className="text-sm text-gray-700">
            <span className="font-semibold">Vare:</span>{' '}
            <span className="font-mono">{item.itemNo}</span> — {item.description1 || '-'}
          </p>
          <p className="text-sm text-gray-700 mt-1">
            <span className="font-semibold">Nåværende type:</span>{' '}
            <span className="inline-flex text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
              {getItemTypeLabel(currentType)}
            </span>
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {ITEM_TYPES.map((opt) => {
          const isCurrent = opt.value === currentType;
          const isSelected = opt.value === selected;
          return (
            <label
              key={opt.value}
              className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors ${
                isCurrent
                  ? 'bg-gray-100 border-gray-200 opacity-60 cursor-not-allowed'
                  : isSelected
                  ? 'bg-red-50 border-red-400 ring-2 ring-red-200'
                  : 'bg-white border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input
                type="radio"
                name="newItemType"
                value={opt.value}
                checked={isSelected}
                disabled={isCurrent}
                onChange={() => setSelected(opt.value)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">
                    {opt.value}: {opt.labelNb}
                  </span>
                  {opt.isTracked && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                      Lagerstyrt
                    </span>
                  )}
                  {isCurrent && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                      Nåværende
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-0.5">{opt.description}</p>
              </div>
            </label>
          );
        })}
      </div>

      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 p-3 rounded-md">
          <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4" />
          Tilbake
        </button>
        <button
          onClick={handleNext}
          disabled={selected === null || loading}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
          Forhåndsvis konsekvenser
        </button>
      </div>
    </div>
  );
};
